import { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, Baseline, Crop, Eraser, PenLine, RotateCcw, Save, Square, Undo2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type EditorTool = 'crop' | 'pen' | 'rect' | 'arrow' | 'text' | 'blur';

type Point = { x: number; y: number };
type Rect = { x: number; y: number; w: number; h: number };
type PendingText = { canvasPoint: Point; left: number; top: number; value: string };

interface ScreenshotEditorProps {
  open: boolean;
  imageDataUrl: string | null;
  onCancel: () => void;
  onSave: (dataUrl: string) => void;
}

const tools: Array<{ id: EditorTool; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { id: 'crop', label: 'Crop', icon: Crop },
  { id: 'pen', label: 'Draw', icon: PenLine },
  { id: 'rect', label: 'Box', icon: Square },
  { id: 'arrow', label: 'Arrow', icon: ArrowUpRight },
  { id: 'text', label: 'Text', icon: Baseline },
  { id: 'blur', label: 'Blur', icon: Eraser },
];

export function ScreenshotEditor({ open, imageDataUrl, onCancel, onSave }: ScreenshotEditorProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const editorSurfaceRef = useRef<HTMLDivElement | null>(null);
  const textInputRef = useRef<HTMLInputElement | null>(null);
  const dragStart = useRef<Point | null>(null);
  const dragSnapshot = useRef<ImageData | null>(null);
  const [tool, setTool] = useState<EditorTool>('rect');
  const [color, setColor] = useState('#ef4444');
  const [history, setHistory] = useState<string[]>([]);
  const [cropRect, setCropRect] = useState<Rect | null>(null);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingText, setPendingText] = useState<PendingText | null>(null);

  useEffect(() => {
    if (!open || !imageDataUrl) return;
    let cancelled = false;

    const drawLoadedImage = (
      image: CanvasImageSource,
      sourceWidth: number,
      sourceHeight: number,
    ) => {
      const canvas = canvasRef.current;
      if (!canvas || cancelled) return;
      const maxWidth = 1800;
      const maxHeight = 1200;
      const scale = Math.min(1, maxWidth / sourceWidth, maxHeight / sourceHeight);
      canvas.width = Math.max(1, Math.round(sourceWidth * scale));
      canvas.height = Math.max(1, Math.round(sourceHeight * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      setHistory([canvas.toDataURL('image/png')]);
      setCropRect(null);
      setLoadError(null);
      setIsLoadingImage(false);
    };

    const load = async () => {
      setIsLoadingImage(true);
      setLoadError(null);
      await new Promise(resolve => requestAnimationFrame(resolve));

      try {
        const loadWithImageElement = () => new Promise<void>((resolve, reject) => {
          const image = new Image();
          image.onload = () => {
            drawLoadedImage(image, image.naturalWidth || image.width, image.naturalHeight || image.height);
            resolve();
          };
          image.onerror = () => reject(new Error('Could not load screenshot image'));
          image.src = imageDataUrl;
        });

        if ('createImageBitmap' in window) {
          try {
            const response = await fetch(imageDataUrl);
            const blob = await response.blob();
            const bitmap = await createImageBitmap(blob);
            drawLoadedImage(bitmap, bitmap.width, bitmap.height);
            bitmap.close();
            return;
          } catch {
            await loadWithImageElement();
            return;
          }
        }

        await loadWithImageElement();
      } catch (error) {
        if (cancelled) return;
        setIsLoadingImage(false);
        setLoadError(error instanceof Error ? error.message : 'Could not load screenshot image');
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [imageDataUrl, open]);

  // Focus only when the text box is first placed. Depending on the whole
  // pendingText object would re-run this on every keystroke and the select()
  // would make the next letter replace everything already typed.
  useEffect(() => {
    if (!pendingText) return;
    requestAnimationFrame(() => {
      textInputRef.current?.focus();
      textInputRef.current?.select();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingText?.canvasPoint]);

  const getContext = () => canvasRef.current?.getContext('2d') || null;

  const pushHistory = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setHistory(current => [...current.slice(-9), canvas.toDataURL('image/png')]);
  };

  const restoreDataUrl = (dataUrl: string) => {
    const canvas = canvasRef.current;
    const ctx = getContext();
    if (!canvas || !ctx) return;
    const image = new Image();
    image.onload = () => {
      canvas.width = image.width;
      canvas.height = image.height;
      ctx.drawImage(image, 0, 0);
    };
    image.src = dataUrl;
  };

  const getPoint = (event: React.PointerEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const bounds = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - bounds.left) * (canvas.width / bounds.width),
      y: (event.clientY - bounds.top) * (canvas.height / bounds.height),
    };
  };

  const drawArrow = (ctx: CanvasRenderingContext2D, start: Point, end: Point) => {
    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    const headLength = 28;
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(end.x, end.y);
    ctx.lineTo(end.x - headLength * Math.cos(angle - Math.PI / 6), end.y - headLength * Math.sin(angle - Math.PI / 6));
    ctx.lineTo(end.x - headLength * Math.cos(angle + Math.PI / 6), end.y - headLength * Math.sin(angle + Math.PI / 6));
    ctx.closePath();
    ctx.fill();
  };

  const drawBlur = (ctx: CanvasRenderingContext2D, rect: Rect) => {
    const x = Math.min(rect.x, rect.x + rect.w);
    const y = Math.min(rect.y, rect.y + rect.h);
    const w = Math.abs(rect.w);
    const h = Math.abs(rect.h);
    if (w < 6 || h < 6) return;
    const pixels = ctx.getImageData(x, y, w, h);
    const temp = document.createElement('canvas');
    temp.width = w;
    temp.height = h;
    const tempCtx = temp.getContext('2d');
    if (!tempCtx) return;
    tempCtx.putImageData(pixels, 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(temp, 0, 0, w, h, x, y, Math.max(1, w / 14), Math.max(1, h / 14));
    ctx.drawImage(canvasRef.current as HTMLCanvasElement, x, y, Math.max(1, w / 14), Math.max(1, h / 14), x, y, w, h);
    ctx.imageSmoothingEnabled = true;
  };

  const drawText = (point: Point, value: string) => {
    const ctx = getContext();
    if (!ctx) return;
    const text = value.trim();
    if (!text) return;
    ctx.fillStyle = color;
    ctx.font = 'bold 34px Arial, sans-serif';
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 6;
    ctx.lineJoin = 'round';
    ctx.strokeText(text, point.x, point.y);
    ctx.fillText(text, point.x, point.y);
    pushHistory();
  };

  const commitPendingText = () => {
    if (!pendingText) return;
    drawText(pendingText.canvasPoint, pendingText.value);
    setPendingText(null);
  };

  const drawPreviewShape = (start: Point, end: Point) => {
    const ctx = getContext();
    const canvas = canvasRef.current;
    if (!ctx || !canvas || !dragSnapshot.current) return;
    ctx.putImageData(dragSnapshot.current, 0, 0);
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const rect = { x: start.x, y: start.y, w: end.x - start.x, h: end.y - start.y };
    if (tool === 'crop') {
      ctx.save();
      ctx.fillStyle = 'rgba(0,0,0,0.35)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.clearRect(Math.min(rect.x, rect.x + rect.w), Math.min(rect.y, rect.y + rect.h), Math.abs(rect.w), Math.abs(rect.h));
      ctx.strokeStyle = '#10b981';
      ctx.strokeRect(Math.min(rect.x, rect.x + rect.w), Math.min(rect.y, rect.y + rect.h), Math.abs(rect.w), Math.abs(rect.h));
      ctx.restore();
    } else if (tool === 'rect' || tool === 'blur') {
      ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
    } else if (tool === 'arrow') {
      drawArrow(ctx, start, end);
    }
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const ctx = getContext();
    if (!canvas || !ctx) return;
    const point = getPoint(event);

    if (tool === 'text') {
      const surface = editorSurfaceRef.current;
      const surfaceBounds = surface?.getBoundingClientRect();
      setPendingText({
        canvasPoint: point,
        left: surfaceBounds ? event.clientX - surfaceBounds.left + surface.scrollLeft : event.clientX,
        top: surfaceBounds ? event.clientY - surfaceBounds.top + surface.scrollTop : event.clientY,
        value: '',
      });
      return;
    }

    dragStart.current = point;
    dragSnapshot.current = ctx.getImageData(0, 0, canvas.width, canvas.height);
    canvas.setPointerCapture(event.pointerId);

    if (tool === 'pen') {
      ctx.strokeStyle = color;
      ctx.lineWidth = 5;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(point.x, point.y);
    }

  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const start = dragStart.current;
    const ctx = getContext();
    if (!start || !ctx || tool === 'text') return;
    const point = getPoint(event);
    if (tool === 'pen') {
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
      return;
    }
    drawPreviewShape(start, point);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const start = dragStart.current;
    const ctx = getContext();
    if (canvas?.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
    if (!start || !ctx || tool === 'text') {
      dragStart.current = null;
      dragSnapshot.current = null;
      return;
    }
    const point = getPoint(event);
    const rect = { x: start.x, y: start.y, w: point.x - start.x, h: point.y - start.y };

    if (tool === 'crop') {
      if (dragSnapshot.current) ctx.putImageData(dragSnapshot.current, 0, 0);
      setCropRect(rect);
    } else if (tool === 'blur') {
      if (dragSnapshot.current) ctx.putImageData(dragSnapshot.current, 0, 0);
      drawBlur(ctx, rect);
      pushHistory();
    } else {
      pushHistory();
    }
    dragStart.current = null;
    dragSnapshot.current = null;
  };

  const undo = () => {
    if (history.length <= 1) return;
    setPendingText(null);
    const next = history.slice(0, -1);
    setHistory(next);
    restoreDataUrl(next[next.length - 1]);
    setCropRect(null);
  };

  const reset = () => {
    if (!history[0]) return;
    setPendingText(null);
    setHistory([history[0]]);
    restoreDataUrl(history[0]);
    setCropRect(null);
  };

  const applyCrop = () => {
    const canvas = canvasRef.current;
    const ctx = getContext();
    if (!canvas || !ctx || !cropRect) return;
    const x = Math.max(0, Math.min(cropRect.x, cropRect.x + cropRect.w));
    const y = Math.max(0, Math.min(cropRect.y, cropRect.y + cropRect.h));
    const w = Math.min(canvas.width - x, Math.abs(cropRect.w));
    const h = Math.min(canvas.height - y, Math.abs(cropRect.h));
    if (w < 20 || h < 20) return;
    const data = ctx.getImageData(x, y, w, h);
    canvas.width = w;
    canvas.height = h;
    ctx.putImageData(data, 0, 0);
    setCropRect(null);
    setPendingText(null);
    pushHistory();
  };

  const save = () => {
    commitPendingText();
    const canvas = canvasRef.current;
    if (!canvas) return;
    onSave(canvas.toDataURL('image/png', 0.9));
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="max-w-6xl">
        <DialogHeader>
          <DialogTitle>Edit screenshot</DialogTitle>
          <DialogDescription>Crop, annotate, label, or blur the screenshot before sending the report.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="flex flex-wrap items-center gap-2">
            {tools.map(item => {
              const Icon = item.icon;
              return (
                <Button
                  key={item.id}
                  type="button"
                  variant={tool === item.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTool(item.id)}
                  className="gap-2"
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </Button>
              );
            })}
            <div className="flex items-center gap-2 ml-auto">
              <Label htmlFor="screenshot-color" className="text-xs">Colour</Label>
              <Input id="screenshot-color" type="color" value={color} onChange={(event) => setColor(event.target.value)} className="w-12 h-9 p-1" />
              <Button type="button" variant="outline" size="sm" onClick={undo} disabled={history.length <= 1}>
                <Undo2 className="w-4 h-4 mr-2" />
                Undo
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={reset}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
            </div>
          </div>

          {cropRect && (
            <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
              <span>Crop area selected. Apply it or continue editing to choose a different crop.</span>
              <Button type="button" size="sm" onClick={applyCrop}>Apply Crop</Button>
            </div>
          )}

          <div ref={editorSurfaceRef} className="relative max-h-[65vh] overflow-auto rounded-lg border bg-slate-100 p-3">
            {isLoadingImage && (
              <div className="absolute inset-3 z-10 grid place-items-center rounded bg-white/80 text-sm text-slate-600">
                Loading screenshot...
              </div>
            )}
            {loadError && (
              <div className="absolute inset-3 z-10 grid place-items-center rounded bg-red-50 p-4 text-center text-sm text-red-700">
                {loadError}. Please upload the screenshot again.
              </div>
            )}
            <canvas
              ref={canvasRef}
              className="mx-auto max-w-full cursor-crosshair rounded bg-white shadow-sm"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            />
            {pendingText && (
              <input
                ref={textInputRef}
                value={pendingText.value}
                onChange={(event) => setPendingText(current => current ? { ...current, value: event.target.value } : current)}
                onBlur={commitPendingText}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') commitPendingText();
                  if (event.key === 'Escape') setPendingText(null);
                }}
                placeholder="Type text"
                className="absolute z-20 min-w-44 rounded-md border border-emerald-500 bg-white px-3 py-2 text-sm font-semibold shadow-lg outline-none ring-2 ring-emerald-200"
                style={{ left: pendingText.left, top: pendingText.top }}
              />
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onCancel}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button type="button" onClick={save}>
              <Save className="w-4 h-4 mr-2" />
              Save Screenshot
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
